/**
 * A list of the current structure set's structures that opens at the
 * pointer on W (see {@link PointerMenu}), in the sidebar's order. Picking
 * a structure selects it as a plain click on its sidebar row does.
 */
import { VisibilityOffOutlined } from '@mui/icons-material';
import { Box, MenuItem, Paper } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import StructureLabel from '~/components/Structures/StructureLabel';
import { refused } from '~/components/toolbar/shared/toolEntry';
import { sortRois } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/ContouringStep/StructureSet/roiSort';
import useRoiSelect from '~/hooks/structure-set/useRoiSelect';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { bulkRoiSelectionActiveState } from '~/state/atoms/structureSet/roiAtoms';
import PointerMenu from './PointerMenu';
import {
    LIST_BORDER_PX,
    LIST_GAP_PX,
    LIST_PAD_PX,
    LIST_ROW_PX,
    LIST_WIDTH_PX,
    layoutList,
    listHeight,
    overScrollbar,
    rowAt,
} from './listMenuGeometry';

const OPEN_KEY = 'w';

export default function StructureQuickMenu() {
    const intl = useIntl();
    const { structureSet, roiNumber: selected } = useActiveEditTarget();
    const bulkSelection = useRecoilValue(bulkRoiSelectionActiveState);
    const select = useRoiSelect();
    const rois = useMemo(
        () => (structureSet ? [...structureSet.roiArray].sort(sortRois) : []),
        [structureSet]
    );
    const currentIndex = rois.findIndex((roi) => roi.roiNumber === selected);
    const current = currentIndex === -1 ? null : currentIndex;

    // The list's scroll, which the rows are found by. Set where the list is
    // placed, and kept in step with the list as it scrolls.
    const [scrollTop, setScrollTop] = useState(0);
    const openingScroll = useRef(0);
    // A callback ref, since the list mounts a commit after the menu opens:
    // the portal it is drawn in only then has a node to draw into.
    const scrollOnMount = useCallback((list: HTMLDivElement | null) => {
        if (list) {
            list.scrollTop = openingScroll.current;
        }
    }, []);

    let refusal: string | undefined;
    if (!structureSet) {
        refusal = intl.formatMessage({ id: 'tool.disabled.noStructureSet' });
    } else if (bulkSelection) {
        refusal = intl.formatMessage({ id: 'tool.disabled.bulkSelection' });
    } else if (rois.length === 0) {
        refusal = intl.formatMessage({
            id: 'structureMenu.disabled.noStructures',
        });
    }

    return (
        <PointerMenu
            openKey={OPEN_KEY}
            placeAt={(pointer) => {
                const layout = layoutList(
                    pointer,
                    rois.length,
                    current,
                    window.innerWidth,
                    window.innerHeight
                );
                openingScroll.current = layout.scrollTop;
                setScrollTop(layout.scrollTop);
                return layout.topLeft;
            }}
            itemAt={(at, topLeft) => rowAt(at, topLeft, rois.length, scrollTop)}
            onPick={(index) => {
                const roi = rois[index];
                if (roi) {
                    select(roi);
                }
            }}
            mayOpen={() =>
                !refused({
                    label: intl.formatMessage({ id: 'structureMenu.label' }),
                    hotkey: OPEN_KEY,
                    disabled: refusal !== undefined,
                    disabledReason: refusal,
                })
            }
            data-testid="structure-quick-menu"
        >
            {(hovered) => (
                <Paper
                    ref={scrollOnMount}
                    elevation={1}
                    role="listbox"
                    aria-label={intl.formatMessage({
                        id: 'structureMenu.label',
                    })}
                    onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
                    onPointerDown={(e) => {
                        // A press on the scrollbar scrolls; kept from the menu,
                        // its release picks nothing and leaves the list open.
                        const box = e.currentTarget.getBoundingClientRect();
                        if (
                            overScrollbar(
                                { x: e.clientX, y: e.clientY },
                                { x: box.left, y: box.top },
                                rois.length
                            )
                        ) {
                            e.stopPropagation();
                        }
                    }}
                    sx={{
                        position: 'absolute',
                        width: LIST_WIDTH_PX,
                        height: listHeight(rois.length),
                        boxSizing: 'border-box',
                        p: `${LIST_PAD_PX}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: `${LIST_GAP_PX}px`,
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        borderRadius: 1,
                        border: `${LIST_BORDER_PX}px solid`,
                        borderColor: 'divider',
                    }}
                >
                    {rois.map((roi, index) => (
                        <MenuItem
                            key={roi.roiNumber}
                            component="div"
                            role="option"
                            aria-selected={index === current}
                            selected={index === current}
                            disableRipple
                            tabIndex={-1}
                            data-testid={`structure-quick-menu-${roi.roiNumber}`}
                            sx={{
                                height: LIST_ROW_PX,
                                minHeight: LIST_ROW_PX,
                                flexShrink: 0,
                                boxSizing: 'border-box',
                                // The row under the pointer is the one a release
                                // picks, found by geometry, so the browser's own
                                // hover never marks another.
                                pointerEvents: 'none',
                                ...(index === hovered &&
                                    index !== current && {
                                        bgcolor: 'action.hover',
                                    }),
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <StructureLabel
                                    roi={roi}
                                    dimmed={!roi.visible}
                                />
                            </Box>
                            {!roi.visible && (
                                <VisibilityOffOutlined
                                    sx={{
                                        fontSize: 14,
                                        color: 'text.secondary',
                                    }}
                                />
                            )}
                        </MenuItem>
                    ))}
                </Paper>
            )}
        </PointerMenu>
    );
}
